import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { ScenarioComponent } from '../../types/components';
import {
  scenarioState,
  ScenarioItem,
} from '../../stores/scenarioState';
import {
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import { useListenTransitionEnd } from '../../effects/useListenTransitionEnd';
import { useClassNameWithLoading } from '../../effects/useClassNameWithLoading';
import Message from '../items/Message';
import TextInput from '../items/TextInput';
import Select from '../items/Select';
import MultiSelect from '../items/MultiSelect';
import OkButton from '../items/OkButton';
import { formState, inputFormState } from '../../stores/formState';
import { useRecoilValueRef } from '../../effects/useRecoilValueRef';
import {
  currentScenarioItemState,
} from '../../stores/displayedScenarioItemState';
import CompleteButton from '../items/CompleteButton';
import TextArea from '../items/TextArea';
import DateInput from '../items/DateInput';
import MedicalQuestionnaireSelect from '../items/MedicalQuestionnaireSelect';
import { pageState } from '../../stores/pageState';
import AddressInput from '../items/AddressInput';
import ScrollBox from '../items/ScrollBox';

export interface BalloonProps {
  item: ScenarioItem;
  current: boolean;
  onCommit: (results: { id: string, value: any }[]) => void;
}

const focusableTypes = ['number']
function getFirstFocusingComponent(item: ScenarioItem) {
  return item.components.filter(i => focusableTypes.includes(i.type))[0];
}

function switchComponent(component: ScenarioComponent, focus: boolean) {
  switch (component.type) {
    case 'message':
      return (<Message key={component.id} componentId={component.id} {...component}/>);
    case 'text':
      return (<TextInput key={component.id} componentId={component.id} focus={focus} {...component}/>);
    case 'address':
      return (<AddressInput key={component.id} componentId={component.id} {...component}/>);
    case 'textarea':
      return (<TextArea key={component.id} componentId={component.id} focus={focus} {...component}/>);
    case 'date':
      return (<DateInput key={component.id} componentId={component.id} focus={focus} {...component}/>);
    case 'select':
      return (<Select key={component.id} componentId={component.id} {...component}/>);
    case 'multiselect':
      return (<MultiSelect key={component.id} componentId={component.id} {...component}/>)
    case 'ok':
      return (<OkButton key={component.id} componentId={component.id} {...component}/>)
    case 'complete':
      return (<CompleteButton key={component.id} componentId={component.id} {...component}/>)
    case 'medical-questionnaire-select':
      return (<MedicalQuestionnaireSelect key={component.id} componentId={component.id} {...component}/>)
    case 'scroll-box':
      return (<ScrollBox key={component.id} componentId={component.id} {...component}/>)
  }
}

function prevent(e: FormEvent) {
  e.preventDefault();
}

const Balloon: React.FC<BalloonProps> = (props) => {
  const style = useRecoilValue(scenarioState).balloon;
  const page = useRecoilValue(pageState);
  const inputForm = useRecoilValueRef(inputFormState(props.item.id));
  const setForm = useSetRecoilState(formState);
  const contentRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLImageElement>(null);

  const baseClasses = useClassNameWithLoading('balloon');

  const [className, setClassName] = useState([`balloon-${props.item.type}`]);
  const isCurrent = props.current;
  useEffect(() => {
    setClassName(state => state.filter(c => c !== 'balloon-current').concat(isCurrent ? ['balloon-current'] : []))
  }, [isCurrent])

  useListenTransitionEnd(contentRef, (e, target) => {
    if (page.location === 'preview') return;
    if (props.item.type === 'opposite') {
      iconRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'})
    }
    if (props.item.type === 'self') {
      target.closest('.balloon')!.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }, [page.location, props.item]);

  const currentScenarioItem = useRecoilValue(currentScenarioItemState);
  const isActive = currentScenarioItem?.id === props.item.id
  useEffect(() => {
    if (!isActive) {
      const current = inputForm.current;
      setForm(prev => {
        return {...prev, ...current};
      })
    }
  }, [isActive, inputForm, setForm]);

  const firstFocusingComponent = getFirstFocusingComponent(props.item);

  return (
    <form className={[...baseClasses, ...className].join(" ")} onSubmit={prevent}>
      <div className="icon">
        <img src={style.icon} ref={iconRef} alt="icon"/>
      </div>
      <div className="content" ref={contentRef}>
        {props.item.components.map(component => switchComponent(component, component === firstFocusingComponent))}
      </div>
    </form>
  );
};

export default Balloon;
